import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  CreditApplicationTemplate,
  getBranchSalesRepKey,
  isFormTemplateType,
} from '@dill/dill-shared';
import { DillButton, DillModal, DillDropdown } from '../../../general/Dill';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { firebaseLargeFileUploadHelper, openDillToast } from '../../../../utils/helpers';
import {
  getCreditApplications,
  sendCreditApplicationTemplateLink,
} from '../../../../redux/services/creditApplicationsService';
import { getSupplierBranches } from '../../../../redux/services/supplierAdditionalDetailsService';
import { useDropzone } from 'react-dropzone';
import { CheckCircle, FileArrowUp, UploadSimple, X } from 'phosphor-react';
import { COLORS } from '../../../../utils/colors';
import SpecificRulesSelector, { SpecificRule } from './SpecificRulesSelector';
import { getCreditApplicationTemplateLabel } from '../CreditApplicationTemplate/CONSTANTS';

const baseStyle = {
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 4,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  outline: 'none',
  transition: 'border .24s ease-in-out',
};

const focusedStyle = { borderColor: '#2196f3' };
const acceptStyle = { borderColor: '#00e676' };
const rejectStyle = { borderColor: '#ff1744' };

const SendCreditApplicationLinkModal = ({
  open,
  handleClose,
  onExternalFormSent,
}: {
  open: boolean;
  handleClose: () => void;
  onExternalFormSent?: () => void;
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const { branches: supplierBranches, loadedSupplierId: loadedSupplierBranchesId } = useAppSelector(
    (state) => state.supplierBranches
  );
  useEffect(() => {
    if (currentSupplier?.id && loadedSupplierBranchesId !== currentSupplier.id) {
      dispatch(getSupplierBranches({ supplierId: currentSupplier.id }));
    }
  }, [currentSupplier?.id, loadedSupplierBranchesId, dispatch]);
  const { creditApplicationTemplates } = useAppSelector(
    (state) => state.creditApplicationTemplates
  );

  const templateOptions = useMemo(() => {
    const options = creditApplicationTemplates
      .filter((t) => !isFormTemplateType(t.applicationType))
      .map((t) => ({
        label: getCreditApplicationTemplateLabel(t),
        value: t.id,
      }));
    // "General Application" lets the customer pick between the credit / COD applications; external
    // forms are only ever sent on their own.
    const applicationCount = creditApplicationTemplates.filter(
      (t) => t.applicationType === 'CREDIT_APPLICATION' || t.applicationType === 'CASH_APPLICATION'
    ).length;
    return applicationCount > 1
      ? [{ label: 'General Application', value: 'general' }, ...options]
      : options;
  }, [creditApplicationTemplates]);

  const [inputFields, setInputFields] = useState<{
    firstName: string;
    lastName: string;
    email: string;
  }>({ firstName: '', lastName: '', email: '' });

  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [errorFields, setErrorFields] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [comment, setComment] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<File[] | null>(null);
  const [specificRules, setSpecificRules] = useState<SpecificRule[]>([]);
  const isExternalFormSelected = creditApplicationTemplates.some(
    (t) => t.id === selectedTemplateId && t.applicationType === 'EXTERNAL_FORM'
  );
  // External forms: the branch and sales rep the application is assigned to — who it routes to
  // after the customer fills it in.
  const [assignedBranchId, setAssignedBranchId] = useState('');
  const [assignedSalesRepId, setAssignedSalesRepId] = useState('');

  const branchOptions = useMemo(
    () =>
      supplierBranches
        .filter((branch) => !!branch?.branchId)
        .map((branch) => ({ value: branch.branchId, label: branch.branchName || branch.branchId }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [supplierBranches]
  );
  // The selected branch's reps (every branch's until one is picked), by getBranchSalesRepKey.
  const salesRepOptions = useMemo(() => {
    const seen = new Set<string>();
    return supplierBranches
      .filter((branch) => !assignedBranchId || branch.branchId === assignedBranchId)
      .flatMap((branch) => branch?.salesRep ?? [])
      .map((rep) => ({ value: getBranchSalesRepKey(rep), label: rep?.name || rep?.erpId || '' }))
      .filter((rep) => !!rep.value && !seen.has(rep.value) && !!seen.add(rep.value))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [supplierBranches, assignedBranchId]);

  // Picking an external form pre-fills the sender, when they're a sales rep, and their branch.
  useEffect(() => {
    if (!isExternalFormSelected || assignedBranchId || assignedSalesRepId || !user?.id) return;
    const ownBranch = supplierBranches.find((branch) =>
      (branch?.salesRep ?? []).some((rep) => rep?.id === user.id)
    );
    if (ownBranch) {
      setAssignedBranchId(ownBranch.branchId);
      setAssignedSalesRepId(user.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExternalFormSelected, supplierBranches, user?.id]);

  const handleCloseModal = () => {
    setInputFields({ firstName: '', lastName: '', email: '' });
    setErrorFields({});
    setSelectedTemplateId('');
    setComment('');
    setIsLoading(false);
    setSpecificRules([]);
    setAssignedBranchId('');
    setAssignedSalesRepId('');
    handleClose();
  };

  const handleFieldChange = (field: keyof typeof inputFields, value: string) => {
    setInputFields((prev) => ({ ...prev, [field]: value }));
    setErrorFields((prev) => ({ ...prev, [field]: '' }));
  };

  const validateFields = () => {
    const errors: { [key: string]: string } = {};
    if (!inputFields.firstName.trim()) {
      errors.firstName = 'First name is required';
    }
    if (!inputFields.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }
    if (!inputFields.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inputFields.email)) {
      errors.email = 'Invalid email format';
    }
    if (!selectedTemplateId) {
      errors.template = 'Application is required';
    }
    if (isExternalFormSelected && branchOptions.length > 0 && !assignedBranchId) {
      errors.branch = 'Branch is required';
    }
    if (isExternalFormSelected && salesRepOptions.length > 0 && !assignedSalesRepId) {
      errors.salesRep = 'Sales rep is required';
    }

    setErrorFields(errors);
    return Object.keys(errors).length === 0;
  };

  const uploadAttachments = async (files: File[]) => {
    const attachments: { url: string; name: string }[] = [];
    for (const file of files) {
      const uploadURL = await firebaseLargeFileUploadHelper({
        blobUrl: URL.createObjectURL(file),
        fileNameWithExtension: `${file.name}`,
        storagePath: 'creditApplications/sendLinkAttAchments',
      });

      if (uploadURL) {
        attachments.push({ url: uploadURL, name: file.name });
      } else {
        throw new Error(`Failed to upload file ${file.name}`);
      }
    }
    return attachments;
  };

  const handleCreditApplicationLink = async () => {
    if (!validateFields()) return;

    try {
      setIsLoading(true);
      let details = {};
      const salesReps = supplierBranches.flatMap((branch) => branch?.salesRep ?? []);

      const salesRepIds = salesReps
        .map((salesRep) => salesRep.id)
        .filter((id): id is string => Boolean(id));
      if (user && salesRepIds.includes(user.id)) {
        details = { ...details, assignedSalesRep: [user.id] };
      }
      // External forms: the branch and rep picked above.
      if (isExternalFormSelected && assignedBranchId) {
        details = { ...details, branchId: assignedBranchId };
      }
      if (isExternalFormSelected && assignedSalesRepId) {
        details = { ...details, assignedSalesRep: [assignedSalesRepId] };
      }
      if (comment.trim() !== '') {
        details = { ...details, comment };
      }
      if (uploadedFiles && uploadedFiles.length > 0) {
        const attachments = await uploadAttachments(uploadedFiles);
        details = { ...details, attachments };
      }
      const results = await dispatch(
        sendCreditApplicationTemplateLink({
          ...inputFields,
          supplierId: currentSupplier?.id || '',
          ...(selectedTemplateId !== 'general' ? { templateId: selectedTemplateId } : {}),
          ...details,
          ...(specificRules.length > 0 ? { specificRules } : {}),
        })
      );
      if (results.type === 'creditApplications/sendCreditApplicationTemplateLink/fulfilled') {
        openDillToast({
          message: `Application link sent successfully`,
          type: 'SUCCESS',
        });
        if (currentSupplier?.id) {
          dispatch(getCreditApplications({ supplierId: currentSupplier.id, isArchived: false }));
        }
        onExternalFormSent?.();
        handleCloseModal();
      } else {
        openDillToast({ message: 'Failed to send link', type: 'ERROR' });
      }
    } catch (err) {
      openDillToast({ message: 'Failed to send link', type: 'ERROR' });
    } finally {
      setIsLoading(false);
    }
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setUploadedFiles(acceptedFiles);
  }, []);

  const {
    getRootProps,
    getInputProps,
    isFocused,
    isDragAccept,
    open: openFilePicker,
    isDragReject,
  } = useDropzone({
    onDrop,
    noClick: true,
    noKeyboard: true,
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject]
  );

  const handleRemoveFile = (index: number) => {
    setUploadedFiles((prev) => {
      if (!prev) return null;
      const updated = [...prev];
      updated.splice(index, 1);
      return updated.length ? updated : null;
    });
  };

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title="Send Application Link"
      width="540px"
      maxWidth="540px"
      footer={
        <div className="flex justify-end w-full gap-2">
          <DillButton
            label="Cancel"
            variant="secondary"
            onClick={handleCloseModal}
            isDisabled={isLoading}
          />
          <DillButton
            label="Send Link"
            onClick={handleCreditApplicationLink}
            isLoading={isLoading}
            isDisabled={isLoading}
          />
        </div>
      }>
      <div className="flex flex-col gap-4">
        <DillDropdown
          label="Applications"
          containerClassName="h-[40px]"
          isRequired
          isSearchable={false}
          value={selectedTemplateId}
          items={templateOptions}
          placeholder="Select application"
          errorText={errorFields.template}
          onSelectChange={(item) => {
            setSelectedTemplateId(item.value);
            setErrorFields((prev) => ({ ...prev, template: '' }));
          }}
        />

        <div className="flex gap-4">
          <AppInputField
            id="firstName"
            label="First Name"
            isRequired
            errorText={errorFields.firstName || ''}
            value={inputFields.firstName}
            onTextChange={(text) => handleFieldChange('firstName', text)}
          />
          <AppInputField
            id="lastName"
            label="Last Name"
            isRequired
            errorText={errorFields.lastName || ''}
            value={inputFields.lastName}
            onTextChange={(text) => handleFieldChange('lastName', text)}
          />
        </div>

        <AppInputField
          id="email"
          label="Email"
          isRequired
          errorText={errorFields.email || ''}
          value={inputFields.email}
          onTextChange={(text) => handleFieldChange('email', text)}
        />

        {isExternalFormSelected && (branchOptions.length > 0 || salesRepOptions.length > 0) && (
          <div className="flex gap-4">
            {branchOptions.length > 0 && (
              <DillDropdown
                label="Branch"
                containerClassName="h-[40px]"
                isRequired
                value={assignedBranchId || null}
                items={branchOptions}
                placeholder="Select branch"
                errorText={errorFields.branch}
                onSelectChange={(item) => {
                  setAssignedBranchId(item.value);
                  // Keep the rep only when they're on the new branch.
                  const branch = supplierBranches.find((b) => b.branchId === item.value);
                  if (
                    !(branch?.salesRep ?? []).some(
                      (rep) => getBranchSalesRepKey(rep) === assignedSalesRepId
                    )
                  ) {
                    setAssignedSalesRepId('');
                  }
                  setErrorFields((prev) => ({ ...prev, branch: '' }));
                }}
              />
            )}
            {salesRepOptions.length > 0 && (
              <DillDropdown
                label="Sales Rep"
                containerClassName="h-[40px]"
                isRequired
                value={assignedSalesRepId || null}
                items={salesRepOptions}
                placeholder="Select sales rep"
                errorText={errorFields.salesRep}
                onSelectChange={(item) => {
                  setAssignedSalesRepId(item.value);
                  setErrorFields((prev) => ({ ...prev, salesRep: '' }));
                }}
              />
            )}
          </div>
        )}

        <AppInputField
          label="Comment"
          value={comment}
          inputType="textarea"
          onTextChange={(val) => setComment(val)}
        />

        {creditApplicationTemplates.length > 0 &&
          selectedTemplateId &&
          !user?.roles?.includes('CREDIT_APP_SALES') && (
            <SpecificRulesSelector
              templates={
                selectedTemplateId === 'general'
                  ? creditApplicationTemplates
                  : creditApplicationTemplates.filter((t) => t.id === selectedTemplateId)
              }
              rules={specificRules}
              onChange={setSpecificRules}
            />
          )}

        <div
          {...getRootProps({ style })}
          className="flex flex-col items-center w-full PRIMARY_50-BG justify-center p-1">
          <input {...getInputProps()} />
          <div className="p-2 rounded-full PRIMARY_100-BG mb-2">
            <FileArrowUp size={14} color={COLORS.PRIMARY_500} />
          </div>
          <div className="flex flex-col items-center">
            <DillButton
              label="Browse Files"
              variant="secondary"
              onClick={openFilePicker}
              icon={<UploadSimple size={16} color={COLORS.PRIMARY_500} />}
            />
            <p className="text-sm mt-1">Choose files or drag 'n' drop some files here</p>
          </div>
        </div>

        {uploadedFiles && (
          <div className="border GREY_200-BORDER rounded p-2">
            {uploadedFiles.map((uploadedFile, i) => (
              <div key={`uploaded_files_${i}`} className="flex items-center justify-between my-1">
                <div className="flex items-center">
                  <CheckCircle color={COLORS.SUCCESS_500} size={20} weight="fill" />
                  <div className="text-sm ml-2">{uploadedFile.name}</div>
                </div>
                <DillButton
                  variant="icon-red"
                  onClick={() => handleRemoveFile(i)}
                  icon={<X size={14} />}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </DillModal>
  );
};

export default SendCreditApplicationLinkModal;
